export enum NegotiationType {
  SALE_WITH_SIM_CARD = 'SALE_WITH_SIM_CARD',
  SALE_WITHOUT_SIM_CARD = 'SALE_WITHOUT_SIM_CARD',
  SIM_CARD = 'SIM_CARD',
  ACCESSORY_SALE = 'ACCESSORY_SALE',
  SERVICES = 'SERVICES',
}

export interface NegotiationTypeOption {
  id: string;
  name: string;
}

export const NEGOTIATION_TYPES: NegotiationTypeOption[] = [
  { id: NegotiationType.SALE_WITH_SIM_CARD, name: 'Venda com SIM Card' },
  { id: NegotiationType.SALE_WITHOUT_SIM_CARD, name: 'Venda sem SIM Card' },
  { id: NegotiationType.SIM_CARD, name: 'SIM Card' },
  { id: NegotiationType.ACCESSORY_SALE, name: 'Venda de Acessórios' },
  { id: NegotiationType.SERVICES, name: 'Serviços' },
];

export interface ICatalogPriceRange {
  from: number;
  to: number | null;
  unitPriceInCents: number;
}

export interface ICatalogSimCard {
  carriers: string[];
  dataAmountMb: number;
  planType: string;
  provider: string;
  priceInBundleInCents: number;
  priceWithoutDeviceInCents: number;
}

export interface ICatalogService {
  monthlyPriceInCents?: number;
  yearlyPriceInCents?: number;
  fixedPriceInCents?: number;
}

export interface ICatalogItem {
  id: string;
  name: string;
  description?: string;
  category: string;
  negotiationType: NegotiationType;
  basePriceCashInCents: number;
  basePriceInstallmentInCents: number;
  basePriceMonthlyInCents: number;
  hasSimCard: boolean;
  simCard?: ICatalogSimCard;
  service?: ICatalogService;
  priceRanges: ICatalogPriceRange[];
  color?: string;
  sku?: string;
}

export interface IConnectivityDetails {
  carrier: string;
  dataAmountMb: number;
  planType: string;
  provider: string;
}

export interface IProposalCartItem {
  productDescription?: string;
  id: string;
  proposalId: string;
  productId: string;
  negotiationType: NegotiationType;
  quantity: number;
  unitPriceCashInCents: number;
  unitPriceInstallmentInCents: number;
  unitPriceMonthlyInCents: number;
  unitPriceYearlyInCents: number;
  unitPriceFixedInCents: number;
  originalPriceCashInCents: number;
  originalPriceInstallmentInCents: number;
  originalPriceMonthlyInCents: number;
  originalPriceYearlyInCents: number;
  originalPriceFixedInCents: number;
  discountPercent: number;
  connectivityDiscountPercent: number;
  subtotalCashInCents: number;
  subtotalInstallmentInCents: number;
  subtotalMonthlyInCents: number;
  subtotalYearlyInCents: number;
  subtotalFixedInCents: number;
  connectivityDetails?: IConnectivityDetails;
  appliedRange?: { from: number; to: number | null } | null;
  createdAt: string;
  updatedAt?: string;
}

export interface ConnectivityOption {
  id: string;
  name: string;
  priceInBundleInCents: number;
  priceWithoutDeviceInCents: number;
  details: IConnectivityDetails;
}

export interface ICartItemBreakdown {
  products: {
    totalCashInCents: number;
    totalInstallmentInCents: number;
    totalItems: number;
    totalQuantity: number;
  };
  accessories: {
    totalCashInCents: number;
    totalInstallmentInCents: number;
    totalItems: number;
    totalQuantity: number;
  };
  connectivity: {
    totalMonthlyInCents: number;
    totalItems: number;
    totalQuantity: number;
  };
  monthlyServices: {
    totalMonthlyInCents: number;
    totalItems: number;
    totalQuantity: number;
  };
  yearlyServices: {
    totalYearlyInCents: number;
    totalItems: number;
    totalQuantity: number;
  };
  fixedServices: {
    totalFixedInCents: number;
    totalItems: number;
    totalQuantity: number;
  };
}

export interface ICartInstallmentBreakdown {
  installmentInCents: number;
  installmentWithExtraCent: number;
  baseInstallmentInCents: number;
}

export interface ICartPaymentSummary {
  paymentType: 'CASH' | 'INSTALLMENTS';
  items: ICartItemBreakdown;
  shippingInCents: number;
  totalDiscountInCents: number;
  cashTotal?: {
    totalInCents: number;
    shippingInCents: number;
  };
  installmentTotal?: {
    totalFinanceableInCents: number;
    downPaymentLabel: string;
    downPaymentInCents: number;
    installments: number;
    installmentBreakdown: ICartInstallmentBreakdown | null;
    totalInCents: number;
  } | null;
  recurringConnectivity?: {
    monthlyPaymentInCents: number;
    dueDay: number;
    totalInCents: number;
  } | null;
  recurringMonthlyServices?: {
    monthlyPaymentInCents: number;
    dueDay: number;
    totalInCents: number;
  } | null;
  recurringYearlyServices?: {
    yearlyPaymentInCents: number;
    dueDay: number;
    totalInCents: number;
  } | null;
  fixedServicesTotal?: {
    totalFixedInCents: number;
    totalInCents: number;
  } | null;
}

export interface ICartCalculateResponse {
  items: IProposalCartItem[];
  totals: {
    totalCashInCents: number;
    totalInstallmentInCents: number;
    totalMonthlyInCents: number;
    totalYearlyInCents: number;
    totalFixedInCents: number;
    totalItems: number;
    totalQuantity: number;
  };
  capex: {
    totalCashInCents: number;
    totalInstallmentInCents: number;
    totalItems: number;
    totalQuantity: number;
  };
  opex: {
    totalMonthlyInCents: number;
    totalYearlyInCents: number;
    totalFixedInCents: number;
    totalItems: number;
    totalQuantity: number;
  };
  breakdown: ICartItemBreakdown;
  isRecurringOnly: boolean;
  paymentSummary: ICartPaymentSummary | null;
}
